/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Chatroom profile POPOVER (Figma 4139:10345) — the small card shown when a
// sender's avatar is tapped. 280px wide panel surface: avatar + local time,
// name + verified badge, job title, ephemeral status, and a Send-message /
// add-contact / more action row. Tapping the NAME escalates to the full
// `ProfileModal` via `onViewProfile`. Purely presentational; the host feeds
// resolved, undefined-safe props.

import { useRef, useState } from 'react';
import { Text, View } from 'react-native';

import { AnchoredPopover } from '../components/AnchoredPopover';
import { ProfileMoreMenu, type ProfileMoreMenuProps } from './ProfileMoreMenu';
import { ProfileHeaderBlock, ProfileHeaderSkeleton } from './ProfileParts';
import type { ProfileActions, ProfileView } from './profileViewTypes';

export interface ProfileCardProps {
  /** Resolved profile, or null while loading / unavailable. */
  readonly view: ProfileView | null;
  /** True while the peer profile is being fetched → render the skeleton. */
  readonly loading?: boolean | undefined;
  /** True while re-fetching the latest profile over already-shown cached data →
   *  render a thin progress line at the bottom (no full skeleton). */
  readonly refreshing?: boolean | undefined;
  readonly actions: ProfileActions;
  /** Tapping the name opens the full profile modal. */
  readonly onViewProfile?: (() => void) | undefined;
  /** ⋯ "More" menu (View full profile / Add to group / Block). */
  readonly moreMenu?: ProfileMoreMenuProps | undefined;
}

/** Width matches the Figma card; the anchored host clamps it to the viewport. */
export const PROFILE_CARD_WIDTH = 280;

const AVATAR_CLASS = 'w-[80px] h-[80px] rounded-[12px]';

export function ProfileCard({
  view,
  loading,
  refreshing,
  actions,
  onViewProfile,
  moreMenu,
}: ProfileCardProps): JSX.Element {
  const [moreOpen, setMoreOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const moreBtnRef = useRef<any>(null);
  const effectiveActions: ProfileActions =
    moreMenu !== undefined ? { ...actions, onMore: () => setMoreOpen((o) => !o) } : actions;

  return (
    <View
      accessibilityLabel={view !== null ? `Profile for ${view.name}` : 'Loading profile'}
      className="relative w-[280px] rounded-2xl border border-light-border-subtle dark:border-dark-border-subtle bg-light-surface-panel dark:bg-dark-surface-panel p-4"
    >
      {view !== null ? (
        <ProfileHeaderBlock
          view={view}
          actions={effectiveActions}
          avatarSizeClassName={AVATAR_CLASS}
          onNamePress={onViewProfile}
          moreRef={moreBtnRef}
        />
      ) : loading === true ? (
        <ProfileHeaderSkeleton avatarSizeClassName={AVATAR_CLASS} />
      ) : (
        <Text className="text-[14px] text-light-text-secondary dark:text-dark-text-secondary">
          Profile unavailable
        </Text>
      )}

      {moreMenu !== undefined ? (
        <AnchoredPopover
          anchorRef={moreBtnRef}
          open={moreOpen}
          onClose={() => setMoreOpen(false)}
          placement="bottom"
          align="end"
        >
          <ProfileMoreMenu
            groups={moreMenu.groups}
            {...(moreMenu.onViewFullProfile !== undefined
              ? {
                  onViewFullProfile: () => {
                    setMoreOpen(false);
                    moreMenu.onViewFullProfile?.();
                  },
                }
              : {})}
            onAddToGroup={(id) => {
              setMoreOpen(false);
              moreMenu.onAddToGroup(id);
            }}
            onBlock={() => {
              setMoreOpen(false);
              moreMenu.onBlock();
            }}
          />
        </AnchoredPopover>
      ) : null}

      {/* Thin "refreshing latest" progress line — shown only when cached data is
          already on screen and a background re-fetch is in flight. */}
      {refreshing === true && view !== null ? (
        <View className="absolute bottom-0 left-0 right-0 h-0.5 overflow-hidden rounded-b-2xl">
          <View className="h-full w-1/3 bg-light-accent-brand dark:bg-dark-accent-brand opacity-80" />
        </View>
      ) : null}
    </View>
  );
}
